import { IDashboardResponse } from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { buildOrderBy, Filter, toDataSourceResult } from 'utils/query_v3';

export type DashboardsFilter = Readonly<{
    corporationId?: string;
    locationId?: string;
}>;

export type DashboardsDataSourceOptions = BaseDataSourceOptions<
    IDashboardResponse,
    DashboardsFilter
>;

const defaultDashboardsSort: DataSourceSort[] = [{ field: 'Name', dir: 'asc' }];

const getDashboardTypeFilter = (level: 'Location' | 'Corporation') =>
    Filter.eq('DashboardTypeName', `${level}PowerBI`);

const queryFn: DataSourceQuery<IDashboardResponse, DashboardsFilter> = ({
    filter: { corporationId, locationId },
    sort,
    page,
    pageLimit,
}) => {
    const level = locationId ? 'Location' : 'Corporation';
    const filter = Filter.and(
        corporationId && Filter.eq('CorporationId', corporationId),
        locationId && Filter.eq('LocationId', locationId),
        getDashboardTypeFilter(level)
    );

    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.getDashboards({ filter, page, pageLimit, orderBy })
    );
};

export type DashboardsPagedDataSource = PagedDataSource<
    IDashboardResponse,
    DashboardsFilter
>;

export type DashboardsInfiniteDataSource = InfiniteDataSource<
    IDashboardResponse,
    DashboardsFilter
>;

export function useDashboardsPagedDataSource({
    defaultSort = defaultDashboardsSort,
    ...options
}: DashboardsDataSourceOptions): DashboardsPagedDataSource {
    return usePagedDataSource({
        defaultSort,
        ...options,
        query: queryFn,
    });
}

export function useDashboardsInfiniteDataSource({
    defaultSort = defaultDashboardsSort,
    ...options
}: DashboardsDataSourceOptions): DashboardsInfiniteDataSource {
    return useInfiniteDataSource({
        defaultSort,
        ...options,
        query: queryFn,
    });
}
